import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const WebsiteAnnotationTool = () => {
  return (
    <>
    <SEOHead
      title="Website Annotation Tool"
      description="Annotate live websites with pinned notes, threaded comments, and auto captured screenshots. Invisible to visitors. Works on any platform."
      path="/website-annotation-tool"
    />
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">Website Annotation Tool</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight mb-5">
          <span className="font-serif italic text-ink/80">Website annotation</span>{' '}
          <span className="font-display font-bold text-ink">on your live site.</span>
        </h1>
        <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">
          Most annotation tools work on static images or proxy copies. Cayda works on the real thing. Your production site, your staging environment, any URL your team can visit.
        </p>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="space-y-14 text-[15px] font-display text-ink-secondary leading-relaxed">

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Why annotations beat screenshots</h2>
            <p className="mb-3">
              A screenshot is a snapshot in time. By the time someone opens it, the page may have changed. The bug may have moved. The layout may look different on their screen. And they definitely can't click on the screenshot to see what's behind a dropdown or a hover state.
            </p>
            <p>
              Annotations on a live page don't have these problems. The note is pinned to the actual element. Anyone on the team can visit the page, see the note exactly where it was placed, and interact with the surrounding context. No version confusion. No "can you take another screenshot?"
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">How Cayda annotations work</h2>
            <p className="mb-3">
              You add Cayda to your site with a single script tag, or through the WordPress plugin or Shopify app. When an authenticated team member visits the page, they see the Cayda pill, a small floating button. Click it, enter annotation mode, and click on any element to drop a note.
            </p>
            <p className="mb-3">
              Each annotation is anchored to a specific DOM element using a smart selector system. If the page layout changes slightly, the annotation follows the element. If the element is removed, the note is preserved in the dashboard with its original screenshot.
            </p>
            <p>
              Notes support five types (bug, design, content, question, and general feedback) each with a distinct color. On the page, these show up as numbered markers. In the dashboard, they show up as filterable, sortable cards.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">No proxy. No extension. No friction.</h2>
            <p className="mb-3">
              Some annotation tools require you to paste a URL into their system and view a proxied copy of your site. This breaks authenticated pages, dynamic content, and anything behind a login. Others need a browser extension, which means everyone on the team has to install it, and it doesn't work on mobile.
            </p>
            <p>
              Cayda is embedded directly on your site. It's part of the page. It works in Chrome, Safari, Firefox, Edge, and on mobile browsers. Your team annotates the real page in its real environment, with all the real data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Context captured automatically</h2>
            <p>
              Every annotation includes the page URL, a full page screenshot, the viewport dimensions, the browser and OS, and the device type. Pro adds file attachments, and Team adds screen recording. All of this context travels with the note: into the dashboard, into Slack notifications, into Jira issues. The person fixing the bug sees exactly what the reporter saw.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Threaded conversations in context</h2>
            <p>
              Each annotation supports threaded replies. When someone responds to a note, the reply appears right there on the page, next to the element in question. No switching to a separate tool to discuss. No searching through Slack history to find the relevant thread. The conversation stays where the issue is.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">How Cayda compares</h2>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-sm font-display">
                <thead>
                  <tr className="border-b border-ink-faint/30">
                    <th className="text-left py-3 pr-4 font-semibold text-ink">Feature</th>
                    <th className="text-center py-3 px-4 font-semibold text-accent">Cayda</th>
                    <th className="text-center py-3 px-4 font-semibold text-ink-muted">Proxy tools</th>
                    <th className="text-center py-3 pl-4 font-semibold text-ink-muted">Extensions</th>
                  </tr>
                </thead>
                <tbody className="text-ink-secondary">
                  {[
                    ['Annotate live production site', '✓', '✗', '✓'],
                    ['Works behind auth / login', '✓', '✗', '✓'],
                    ['No install for reviewers', '✓', '✓', '✗'],
                    ['Invisible to visitors', '✓', 'N/A', 'N/A'],
                    ['Project management dashboard', '✓', 'Partial', 'Varies'],
                    ['Auto captures browser context', '✓', 'Partial', 'Varies'],
                    ['SPA and page transition support', '✓', '✗', 'Varies'],
                    ['Element anchored notes', '✓', '✗', '✗'],
                  ].map(([feature, cayda, proxy, ext]) => (
                    <tr key={feature} className="border-b border-ink-faint/10">
                      <td className="py-2.5 pr-4 text-ink">{feature}</td>
                      <td className="py-2.5 px-4 text-center text-accent font-medium">{cayda}</td>
                      <td className="py-2.5 px-4 text-center text-ink-muted">{proxy}</td>
                      <td className="py-2.5 pl-4 text-center text-ink-muted">{ext}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Platform support</h2>
            <p>
              One script, every platform. WordPress and Shopify both have one click installs with no code required. For Squarespace, Webflow, Wix, Next.js, React, Vue, and static HTML, you paste a single line. Cayda handles single page apps, hash routing, and platform specific page transitions out of the box. Read the full <Link to="/compatibility" className="text-accent hover:text-accent-hover underline underline-offset-2 transition-colors">compatibility guide</Link> for details.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Pricing</h2>
            <p>
              Free for up to 2 sites, 50 notes each, and 3 team members. Pro is $9/month base plus $5 per member (billed annually) for unlimited sites, notes, file attachments, and Slack integration. Team is $14/month base plus $6 per member (billed annually) and adds screen recording, guest access, and Jira/Asana integrations. Monthly billing also available. No credit card required to start.
            </p>
          </section>
        </div>

        <div className="mt-14"><RelatedLinks current="/website-annotation-tool" /></div>

        {/* CTA */}
        <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
          <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Annotate your site in 30 seconds.</h3>
          <p className="text-sm font-display text-ink-muted mb-5">Add your URL, drop in one script, and start leaving notes.</p>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-faint/20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default WebsiteAnnotationTool;
